import React, { Component } from "react"
import PropTypes from "prop-types"
import { Portal } from "react-portal"
import validator from "is-my-json-valid"
import { history } from "utils/history"
import { IS_BROWSER } from "constants/environment"
import { SERVICE_SCHEDULE_ERRORS } from "constants/errors"
import { DEFAULT_THRESHOLD_NORMAL } from "constants/layout"
import { withRadium, DetectCompact, SearchParams } from "higher-order-components"
import {
  ServiceModelDetails,
  ServiceDetails,
  ExpandableInfo,
  HeadingBlock
} from "containers"
import { Button, InputText, InputSelect, FormGroup, Section, P, Space } from "AtomicComponents"
import exposeGlobal from "apis/expose-global"
import serviceCalculatorSchema from "./data-schema.js"
import spinnerSvg from "./spinner.svg"
import close from "./close.svg"

const validateServiceData = validator(serviceCalculatorSchema)

const scheduledOnly = s =>
  s.factoryCodeEntity.type === "Scheduled Maintenance" && s.factoryCodeEntity.distance > 0

const VinLocator = () => {
  const descriptionText = `
      The Vehicle Identification Number [VIN] is a unique
      number that identifies your vehicle. Please refer
      to the Specifications section of your owner's manual
      for the exact location of this number.
    `
  const mainText = "Where is my VIN located?"
  return <ExpandableInfo mainText={mainText} description={descriptionText} />
}

export class ServiceCalculatorOwner extends Component {
  static propTypes = {
    endpoint: PropTypes.string,
    errorMessageNOTFOUND: PropTypes.string,
    errorMessageMULTIPLERESULTS: PropTypes.string,
    termsAndConditions: PropTypes.string,
    serviceBookingUrl: PropTypes.string,
    dealerSearchUrl: PropTypes.string,
    vin: PropTypes.string,
    factoryCode: PropTypes.string,
    compact: PropTypes.bool
  }

  static defaultProps = {
    endpoint: "/api/ownerserviceschedule?vinRego=",
    errorMessageNOTFOUND: SERVICE_SCHEDULE_ERRORS.NOTFOUND,
    errorMessageMULTIPLERESULTS: SERVICE_SCHEDULE_ERRORS.MULTIPLERESULTS
  }

  constructor(props) {
    super(props)
    this.state = {
      displaySpinner: false,
      serviceData: null,
      factoryCode: null,
      vinOrRegoInput: "",
      activeFactoryCode: null,
      isUpdatingVINRego: false
    }

    this.fetchServicesForVinOrRego = () => {
      if (this.state.vinOrRegoInput && this.state.vinOrRegoInput !== "") {
        const url = `${this.props.endpoint}${encodeURIComponent(this.state.vinOrRegoInput)}`
        this.setState({
          errorMessage: "",
          displaySpinner: true,
          isPopupOpen: false
        })
        const options = {
          method: "GET",
          headers: {
            "content-type": "application/json"
          }
        }

        /** @todo convert this to use apis/koala/fetchKoalaData * */
        fetch(url, options)
          .then((response) => {
            if (response.status) return response.json()

            throw new Error(`Unfortunately the service is unavailable. Please try again later or contact your Mazda Dealer (Status ${response.status}).`)
          })
          .then((json) => {
            const { errorCode } = json
            const isValid = validateServiceData(json)
            /** @todo Handle this in validateServiceData if possible */
            const preferErrorCode =
              ["NOTFOUND", "MULTIPLERESULTS"].indexOf(errorCode || "") !== -1 || json.errorMessage

            if (!isValid && !preferErrorCode) {
              this.setState({
                displaySpinner: false,
                errorMessage:
                  "Unfortunately data received from the server is invalid. Please try again later or contact your Mazda Dealer"
              })

              return
            }

            const errorMessage = json.errorMessage
              ? json.errorMessage
              : json.errorCode === "NOTFOUND"
                ? this.props.errorMessageNOTFOUND
                : json.errorCode === "MULTIPLERESULTS"
                  ? this.props.errorMessageMULTIPLERESULTS
                  : null

            this.setState(
              {
                displaySpinner: false,
                errorMessage,
                serviceData: errorMessage ? null : json
              },
              () => this.syncUrl()
            )
          })
          .catch((error) => {
            this.setState({
              displaySpinner: false,
              errorMessage:
                "The VIN/Rego look up is currently unavailable, please try again later or contact your nearest dealer."
            })
            console.warn(`URL: ${url}`, error)
          })
      }
    }

    this.setVinOrRego = (value) => {
      this.setState({ vinOrRegoInput: value ? value.trim() : "", errorMessage: "" })
    }

    this.handleNext = () => {
      this.setState({ isUpdatingVINRego: false })
      this.fetchServicesForVinOrRego()
    }

    // If user click on "Change VIN/Rego" button, reset the state of the component
    this.handleChangeVinOrRego = () => {
      this.setState({
        displaySpinner: false,
        serviceData: null,
        factoryCode: null,
        vinOrRegoInput: "",
        activeFactoryCode: null,
        isUpdatingVINRego: true
      })
      // Remove all params
      this.props.search.pushParam({
        VIN: undefined,
        factoryCode: undefined,
        asmc: undefined
      })
    }

    this.setService = (s) => {
      this.setState(
        {
          activeFactoryCode: s
        },
        () => this.syncUrl()
      )
    }

    this.syncUrl = () => {
      if (!this.state.serviceData || !IS_BROWSER) return

      if (this.state.serviceData.serviceItems) {
        const activeService = this.state.serviceData.serviceItems.find(s => s.factoryCode === this.state.activeFactoryCode)

        if (!activeService) return

        this.props.search.pushParam({
          factoryCode: this.state.activeFactoryCode,
          VIN: this.state.serviceData.vehicle.vin,
          asmc: undefined
        })
      }
    }
  }

  componentDidMount() {
    if (!IS_BROWSER) return
    exposeGlobal("getCurrentFormFields", () => {
      const { activeFactoryCode } = this.state
      return { activeFactoryCode }
    })
    const searchParams = this.props.search.query
    const vin = this.props.vin || searchParams.VIN
    const factoryCode = this.props.factoryCode || searchParams.factoryCode || ""

    if (vin) {
      this.setState(
        {
          vinOrRegoInput: vin,
          activeFactoryCode: factoryCode
        },
        () => {
          this.fetchServicesForVinOrRego()
        }
      )
    }
  }

  renderServiceSelect() {
    const options = this.state.serviceData.serviceItems.filter(scheduledOnly).map((s, index) => ({
      name: s.factoryCodeEntity.description,
      code: s.factoryCodeEntity.code,
      label: s.factoryCodeEntity.description,
      value: s.factoryCodeEntity.code,
      index
    }))
    const activeServiceIndex = options.findIndex(s => s.code === this.state.activeFactoryCode)

    return (
      <InputSelect
        label="Please Select"
        defaultValue={{value: this.state.activeFactoryCode}}
        options={options}
        onChange={this.setService} />
    )
  }

  isSPG() {
    try {
      // trying to guess MSS vs SPG service type
      // MSS factoryCode starts with 'S' and SPG starts with 'M' (yes, it's counterintuitive)
      return (
        this.state.serviceData &&
        this.state.serviceData.serviceItems.some(si => si.factoryCode[0] === "M")
      )
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.log(e)
      return false
    }
  }

  updateSelectedAddtionalServices = (selectedAdditionalServices) => {
    this.setState({ selectedAdditionalServices }, () => {
      this.props.search.pushParam({
        asmc: Object.keys(selectedAdditionalServices).filter(item => selectedAdditionalServices[item])
      })
    })
  }
  generateBookServiceHref = () => {
    const { selectedAdditionalServices } = this.state
    const asmcArray =
      selectedAdditionalServices &&
      Object.keys(selectedAdditionalServices).filter(k => selectedAdditionalServices[k])
    const asmcQueryString = asmcArray && asmcArray.map(item => `&asmc=${item}`).join("")
    const formHref = `${this.props.serviceBookingUrl}?vinrego=${this.state.serviceData.vehicle
      .vin}&serviceCode=${this.state.activeFactoryCode}${asmcQueryString}`
    return formHref
  }

  render() {
    const { compact } = this.props
    const renderInput =
      !this.state.serviceData ||
      this.state.serviceData.serviceItems.length === 0 ||
      this.state.isUpdatingVINRego
    const renderSelect =
      this.state.serviceData &&
      this.state.serviceData.serviceItems.length > 0 &&
      !this.state.errorMessage &&
      !this.state.isUpdatingVINRego

    const showDetails =
      this.state.serviceData &&
      !this.state.displaySpinner &&
      !this.state.isUpdatingVINRego &&
      !this.state.errorMessage &&
      this.state.activeFactoryCode

    const mssDisclaimer =
      this.state.serviceData && !this.isSPG()
        ? "The total service price is the Base Scheduled Maintenance, plus any Additional Scheduled Maintenance items that are required."
        : null

    return (
      <div style={styles.container}>
        <HeadingBlock
          tag="h3"
          value={this.state.isUpdatingVINRego ? "Update your car details" : this.props.title}
          textAlign="center" />
        <div
          style={{
            padding: "1em",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}>
          {!this.state.isUpdatingVINRego &&
          this.state.serviceData &&
          this.state.serviceData.vehicle ? (
            <ServiceModelDetails
              model={`${this.state.serviceData.vehicle.modelGroup} ${this.state.serviceData.vehicle
                .grade}`}
              vin={this.state.serviceData.vehicle.vin}
              registration={this.state.serviceData.vehicle.rego}
              compact={compact} />
          ) : this.state.isUpdatingVINRego ? (
            <div style={styles.textCenter}>
              Enter a different VIN/Rego to get a new set of servicing costs.
            </div>
          ) : (
            <div style={styles.textCenter}>{this.props.description}</div>
          )}
          {mssDisclaimer && <div style={styles.textCenter}>{mssDisclaimer}</div>}
          <div style={styles.regoInput}>
            {renderInput && (
              <div
                style={{
                  flex: "1 1 auto" /* auto can be changed to 100% when support for ios 9 drops */,
                  marginRight: "2em"
                }}>
                <InputText
                  onKeyDown={(e) => {
                    e.key === "Enter" && this.handleNext()
                  }}
                  label="Enter your VIN/Rego"
                  value={this.state.vinOrRegoInput}
                  onChange={this.setVinOrRego} />
              </div>
            )}
            {renderInput && (
              <div
                style={{
                  flex: "0 3 auto" /* auto can be changed to 100% when support for ios 9 drops */
                }}>
                <Button
                  styleName="black"
                  label={this.state.isUpdatingVINRego ? "update" : "next"}
                  onClick={this.handleNext}
                  display={"block"}
                  disabled={!this.state.vinOrRegoInput || this.state.displaySpinner}
                  fullWidth />
              </div>
            )}
            <FormGroup>
              {renderSelect && this.renderServiceSelect()}
            </FormGroup>
          </div>
          {renderInput && <VinLocator />}
          {this.state.isPopupOpen && (
            <Portal>
              <div style={styles.popup}>
                <div
                  style={styles.popupCloseButton}
                  onClick={() => this.setState({ isPopupOpen: false })} />
                <div>
                  <HeadingBlock value={"not quite right?"} textAlign="center" />
                  <HeadingBlock
                    tag="h6"
                    value={"update your selection to get new set of servicing costs"}
                    textAlign="center" />
                  <input
                    type="text"
                    onKeyDown={(event) => {
                      event.key === "Enter" && this.handleNext()
                    }}
                    placeholder="YOUR VIN/REGO"
                    value={this.state.vinOrRegoInput}
                    onChange={this.setVinOrRego}
                    style={[styles.input, { flexGrow: 1, width: "100%" }]} />
                  <Button
                    styleName="black"
                    label="next"
                    onClick={this.handleNext}
                    disabled={!this.state.vinOrRegoInput || this.state.displaySpinner}
                    fullWidth />
                </div>
              </div>
            </Portal>
          )}
          {!renderInput && (
            <div
              style={[styles.changeVinRego, styles.changeVinRegoDesktop]}
              onClick={this.handleChangeVinOrRego}>
              <span>Change VIN/REGO ›</span>
            </div>
          )}
          {!renderInput && (
            <div
              style={[styles.changeVinRego, styles.changeVinRegoMobile]}
              onClick={() => this.setState({ isPopupOpen: true })}>
              <span>Change VIN/REGO ›</span>
            </div>
          )}
          {this.state.displaySpinner && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                margin: "1em"
              }}>
              <img src={spinnerSvg} style={{ width: "5vw", height: "5vw", margin: "5em" }} />
            </div>
          )}
          {this.state.errorMessage && (
            <div data-test="error-message" style={styles.error}>
              {this.state.errorMessage}
            </div>
          )}
        </div>
        {showDetails && (
          <Section padding backgroundColor="#ffffff">
            <ServiceDetails
              data={this.state.serviceData}
              factoryCode={this.state.activeFactoryCode}
              selectAdditionalItems
              updateSelectedAddtionalServices={this.updateSelectedAddtionalServices} />
            <Space />
            <P>Further details or need help please contact a Dealer</P>
            <div style={styles.mainCTAButtons}>
              <div
                style={{
                  textAlign: "center",
                  padding: "0.5em",
                  boxSizing: "border-box"
                }}>
                <Button
                  label="Request a service"
                  fullWidth
                  styleName="red"
                  href={this.generateBookServiceHref()} />
              </div>
              <div
                style={{
                  textAlign: "center",
                  padding: "0.5em",
                  boxSizing: "border-box"
                }}>
                <Button
                  label="Find a dealer"
                  fullWidth
                  styleName="blackOutlined"
                  href={this.props.dealerSearchUrl} />
              </div>
            </div>
            {this.props.termsAndConditions && (
              <div style={{ backgroundColor: "white", padding: "1.5em 0 2em" }}>
                <div style={{ maxWidth: "70em", margin: "auto" }}>
                  <ExpandableInfo
                    color="rgb(170, 170, 170)"
                    mainText="* Read full terms and conditions"
                    description={this.props.termsAndConditions} />
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    )
  }
}

export default withRadium(
  DetectCompact(),
  SearchParams({ exposeParams: ["factoryCode", "VIN", "asmc"] })
)(ServiceCalculatorOwner)

const breakpoint1 = `@media (min-width: ${DEFAULT_THRESHOLD_NORMAL}px)`

const styles = {
  container: {
    backgroundColor: "#f5f5f5",
    paddingTop: "3em",
    [breakpoint1]: {
      paddingTop: "5.5em"
    }
  },
  textCenter: {
    textAlign: "center",
    fontWeight: 200,
    padding: ".6em 1em 1em 1em"
  },
  error: {
    textAlign: "center",
    fontWeight: 200,
    color: "#910A2D",
    padding: "1em",
    margin: "1em auto",
    lineHeight: 1.8
  },
  mainCTAButtons: {
    boxSizing: "border-box",
    display: "flex",
    backgroundColor: "white",
    justifyContent: "center",
    flexDirection: "column",
    [breakpoint1]: {
      flexDirection: "row"
    }
  },
  mainCTAButtonsFiller: {
    display: "none",
    [breakpoint1]: {
      flexGrow: 1,
      display: "block"
    }
  },
  regoInput: {
    width: "100%",
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    [breakpoint1]: {
      flexDirection: "row",
      maxWidth: "30em",
      alignItems: "center"
    }
  },
  popup: {
    position: "fixed",
    left: "0px",
    right: "0px",
    bottom: "0px",
    top: "0px",
    padding: "2em",
    backgroundColor: "#e5e6e9",
    zIndex: "100"
  },
  popupCloseButton: {
    width: "100%",
    height: "2em",
    padding: "0em",
    backgroundImage: `url(${close})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "center right",
    cursor: "pointer",
    ":hover": {
      opacity: ".5"
    }
  },
  changeVinRego: {
    fontWeight: 600,
    letterSpacing: 1.8,
    color: "#aaaaaa",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "81%",
    textTransform: "uppercase",
    padding: "1em 1em 3em"
  },
  changeVinRegoDesktop: {
    [breakpoint1]: {
      display: "block"
    },
    display: "none"
  },
  changeVinRegoMobile: {
    [breakpoint1]: {
      display: "none"
    },
    display: "block"
  }
}



// WEBPACK FOOTER //
// ./src/containers/service-calculator-owner/index.jsx